/**
 * O contador de respostas não lidas do Atendimento (D3, passo 19 — peça I): o número do menu, do botão "Menu" e de cada conversa.
 * Fonte única `GET /portal/atendimentos/nao-lidas`; o socket o mantém (`somarRespostaNova`/`aplicarLidas`) e o polling de 30 s é a reserva
 * (pausa com o socket vivo). Só existe para quem tem `portal:atendimento:view`: sem ela nem busca. Falha da busca: o selo some (0) —
 * nunca mostra o número velho.
 */
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { intervaloDeReserva, usePortalSocketVivo } from "@/hooks/usePortalSocketVivo";
import { usePortalPermissions } from "@/hooks/usePortalPermissions";
import {
  CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO,
  totalDeNaoLidas,
  zerarSessaoNoCache,
} from "@/lib/portal/naoLidasDoAtendimento";
import { portalAtendimentosApi } from "@/services/portalAtendimentosApi";

const SEM_SESSOES: Record<string, number> = {};

export function useNaoLidasDoAtendimento() {
  const qc = useQueryClient();
  const { has } = usePortalPermissions();
  const habilitado = has("portal:atendimento:view");
  const socketVivo = usePortalSocketVivo();

  const consulta = useQuery({
    queryKey: CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO,
    queryFn: () => portalAtendimentosApi.naoLidas(),
    enabled: habilitado,
    staleTime: 15_000,
    refetchInterval: intervaloDeReserva(socketVivo),
  });

  const zerarSessao = useCallback((sessionId: string) => zerarSessaoNoCache(qc, sessionId), [qc]);

  const dados = habilitado && !consulta.isError ? consulta.data : undefined;
  return {
    total: totalDeNaoLidas(dados),
    porSessao: dados?.por_sessao ?? SEM_SESSOES,
    zerarSessao,
  };
}
